import { OperationCountValue } from '../value-objects/OperationCountValue';
import {
  MAINTENANCE_OPERATION_DISPLAY_CONFIG,
  type MaintenanceDisplayItem,
} from '@/features/panels/constants/panelDetailTab.constants';

// ========== 型定義 ==========

/** APIから返されるメンテナンスデータの生データ型 */
export interface MaintenanceApiData {
  readonly panelId: number;
  readonly receivedAt: string | null;
  readonly maintenance: MaintenanceApiOperationCounts | null;
}

/** APIレスポンスの動作回数オブジェクト */
interface MaintenanceApiOperationCounts {
  readonly [fieldName: string]: number | null;
}

interface PanelMaintenanceConstructorParams {
  readonly panelId: number;
  readonly receivedAt: Date | null;
  readonly operationCounts: readonly OperationCountValue[];
}

// ========== エンティティ ==========

/**
 * メンテナンスデータエンティティ
 *
 * APIレスポンスからファクトリメソッドで生成し、
 * 4つのOperationCountValue値オブジェクトを保持する。
 * バッファ項目は含めない。
 */
export class PanelMaintenance {
  private readonly panelId: number;
  private readonly receivedAt: Date | null;
  private readonly operationCounts: readonly OperationCountValue[];

  private constructor(params: PanelMaintenanceConstructorParams) {
    this.panelId = params.panelId;
    this.receivedAt = params.receivedAt;
    this.operationCounts = params.operationCounts;
  }

  /**
   * APIレスポンスからエンティティを生成
   *
   * MAINTENANCE_OPERATION_DISPLAY_CONFIG を参照し、
   * 各フィールドに対応するOperationCountValueを生成する。
   */
  public static createFromApiResponse(apiData: MaintenanceApiData): PanelMaintenance {
    const maintenanceValues = apiData.maintenance;

    const operationCounts = MAINTENANCE_OPERATION_DISPLAY_CONFIG.map(
      (config) => OperationCountValue.create({
        displayName: config.displayName,
        count: maintenanceValues !== null
          ? (maintenanceValues[config.fieldName] ?? null)
          : null,
        unit: config.unit,
      })
    );

    return new PanelMaintenance({
      panelId: apiData.panelId,
      receivedAt: apiData.receivedAt !== null ? new Date(apiData.receivedAt) : null,
      operationCounts,
    });
  }

  public getPanelId(): number {
    return this.panelId;
  }

  public getReceivedAt(): Date | null {
    return this.receivedAt;
  }

  /**
   * プレゼンテーションモデルに変換
   */
  public toPresentationModel(): readonly MaintenanceDisplayItem[] {
    return this.operationCounts.map((operationCount) => operationCount.toPresentationModel());
  }
}
